import classnames from 'classnames'
import classNames from 'classnames'
import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Image from '@src/quince-components/src/atoms/image'
import Link from '@src/quince-components/src/atoms/link'
import { NAV_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import GridSlider from '@src/SectionRenderer/sections/gridSlider'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { throttle } from '@src/utils/functions/utils'

import { SubCollection } from '../../domains/Collection'

import styles from './subCollectionCardList.module.scss'

interface SubCollectionCardListProps {
  subCollections: SubCollection[]
  className?: string
  onCardClick: (subCollection: SubCollection) => void
  slug: string
}

const SubCollectionCardList = ({ subCollections = [], className, onCardClick }: SubCollectionCardListProps) => {
  const path = usePathname()
  const deviceInfo = useDeviceInfo()
  const [intersectRef, isNotScrolledDown] = useIntersect(null, '0px', 0, true) as [
    { current: React.ReactNode | null },
    boolean,
  ]
  const isScrolledDown = !isNotScrolledDown

  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = !isDesktop

  const hasSlider = !isDesktop || subCollections.length > 9

  const classes = classnames(styles.container, className, {
    [styles.container__flat]: !hasSlider,
    [styles.container__slider]: hasSlider,
  })

  const imageMaxWidth = getMaxWidthInPx({
    maxWidth: styles.subCollectionCardImageWidth,
    deviceType: deviceInfo.deviceType,
  })

  const [pageYOffset, setPageYOffset] = useState(0)
  const [isScrollingDown, setIsScrollingDown] = useState<boolean>()

  const scrollListener = useCallback((pgY: number) => {
    const currentScrollPos = window.scrollY
    if (pgY > currentScrollPos) {
      setIsScrollingDown(false)
    } else if (pgY < currentScrollPos) {
      setIsScrollingDown(true)
    }
    setPageYOffset(currentScrollPos)
  }, [])

  const { current: throttledScroll } = useRef(throttle(scrollListener, 50))

  useEffect(() => {
    if (isDesktop) return
    const listener = () => {
      throttledScroll(pageYOffset)
    }
    window.addEventListener('scroll', listener)
    return () => {
      window.removeEventListener('scroll', listener)
    }
  }, [throttledScroll, pageYOffset, isDesktop])

  const renderCards = () => {
    const selectedSubcollection = subCollections.find(subCollection => path === subCollection.slug)
    const adjustedFilteredSubCollections = isDesktop
      ? subCollections
      : selectedSubcollection
        ? [selectedSubcollection, ...subCollections.filter(sc => sc !== selectedSubcollection)]
        : subCollections
    return adjustedFilteredSubCollections.map((subCollection, index) => {
      const imageProps = ImageDomain.makeImageProps(subCollection.imageDesktop)
      const isSelected = path === subCollection.slug
      return (
        <Link
          className={classnames(styles.subCollectionCard, {
            [styles.subCollectionCard__withBorder]: !hasSlider,
            [styles.subCollectionCard__selected]: isSelected,
            [styles.subCollectionCard__selected__flat]: !hasSlider && isSelected,
            [styles.subCollectionCard__selected__slider]: hasSlider && isSelected,
            [styles.subCollectionCard__lastCard]: index === adjustedFilteredSubCollections.length - 1,
          })}
          key={subCollection.id}
          onClick={() => {
            trackEvent(TELEMETRY_EVENT_NAMES.SUBCOLLECTION_CARD_CLICK, {
              slug: subCollection.slug,
              title: subCollection.title,
            })
            onCardClick(subCollection)
          }}
        >
          <h5
            className={styles.subCollectionCard__text}
            data-content-type="sub-collection-card-title"
            data-slug={subCollection.slug}
            data-active={isSelected}
          >
            {subCollection.title}
          </h5>
          {isDesktop ? (
            <Image
              alt={imageProps.alt}
              {...imageProps}
              className={styles.subCollectionCard__image}
              maxWidth={imageMaxWidth}
              loading="eager"
            />
          ) : null}
        </Link>
      )
    })
  }
  const getElementHeight = (elementQuery: string) => {
    if (isBrowser() && document.querySelector(elementQuery)) {
      return document.querySelector(elementQuery)?.getBoundingClientRect().height
    }
    return 0
  }

  const computedStyles = useMemo(() => {
    if (!isScrolledDown || isDesktop) return {}
    if (isScrollingDown) {
      return {
        top: '-40px',
      }
    }
    const filterHeight = getElementHeight('#stickyWrapperNew') ?? 0
    const bannerHeight = getElementHeight('#site-primary-header') ?? 0

    const totalTop = filterHeight + bannerHeight

    return {
      position: 'fixed',
      top: `${totalTop}px`,
      'z-index': '99',
      background: 'white',
      'padding-bottom': '1.6rem',
      transition: 'top 0.3s ease-in-out 0s',
      'border-bottom': '0.1rem solid rgb(211,210,210)',
      width: '100%',
    }
  }, [isScrolledDown, isScrollingDown, isDesktop]) as React.CSSProperties

  return (
    <>
      {hasSlider ? (
        <div style={computedStyles}>
          <GridSlider
            navPosition={NAV_POSITION.LEFT_RIGHT}
            hideBorderTop
            hideCenterLine
            className={classes}
            swiperStyles={styles.swiperStyles}
            slideClassName={styles.slideClassName}
            prevIconClass={classNames(styles.iconSize, styles.prevIcon)}
            nextIconClass={styles.iconSize}
            speed={isMobile ? 200 : undefined}
            swiperId="subcollection_carousel"
            isChevronIcon={true}
            isIconWithBG={true}
          >
            {renderCards()}
          </GridSlider>
        </div>
      ) : (
        <div style={computedStyles} className={classes}>
          {renderCards()}
        </div>
      )}
      <div ref={intersectRef}></div>
    </>
  )
}

export default SubCollectionCardList
